import {
  ActionMenu,
  IslandsContainer,
  ProcessInfo
} from '@/ui/components/process-details'
import { AuthorizeBanner } from '@/ui/components/process-details/authorize-banner'
import { UnauthorizedStatusBanner } from '@/ui/components/process-details/unauthorized-status-banner'
import { ReturnedItem, ReturnProcess } from '@/domain/returns'
import { clsx } from 'clsx'
import { RestockActionBar } from './restock-action-bar'
import { useState } from 'react'
import { RestockItem, RestockLocation } from '@/domain/restock'
import { NewReview, PerformedReview } from '@/domain/review'
import { useAppSelector } from '@/redux/hooks'
import {
  useFeatureFlag,
  isDashboardBlocked
} from '@/application/use-feature-flag'
import { BlockedDashboardOverlay } from '../overlays/blocked-dashboard-overlay'
import { PbLogisticsSummary } from '@itsrever/rever-api'
import { ClaimBanner } from './claim-banner'
import { ExchangeShippedBanner } from './exchange-shipped-banner'

const defaultSelectedRestockBrands = ['sorellasthebrand', 'rever']

interface ProcessDetailsProps {
  processDetails?: ReturnProcess
  processLogistics?: PbLogisticsSummary
  restocks?: RestockItem[]
  reviews?: PerformedReview[]
  restockLocations?: Map<string, RestockLocation[]>
}

export const ProcessDetails: React.FC<ProcessDetailsProps> = ({
  processDetails,
  processLogistics,
  restocks,
  reviews,
  restockLocations
}) => {
  const [reviewMode, setReviewMode] = useState(false)
  const [restockMode, setRestockMode] = useState(false)
  const [unauthorizeMode, setUnauthorizeMode] = useState(false)
  const [authorizeMode, setAuthorizeMode] = useState(false)
  const [editAmount, setEditAmount] = useState(false)
  const groupItems = !(reviewMode || restockMode)

  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'process-details'
  })

  const { returnedItems, notReceivedItems, newReviewsList, newRestocksList } =
    useCalculateProductsActions(
      processDetails,
      reviews ?? [],
      restocks ?? [],
      restockLocations ?? new Map<string, RestockLocation[]>(),
      groupItems
    )

  const [newRestocks, setNewRestocks] = useState(newRestocksList)

  const unauthorized = processDetails?.unauthorized ?? false
  const isCancelable =
    processDetails?.status === 'STARTED' &&
    !processDetails.hasIrreversibleRefund

  function isRestocked() {
    const totalReturnedItems =
      processDetails?.returnedItems?.reduce((acc, item) => {
        return (acc += item.quantity ?? 1)
      }, 0) ?? 0
    const totalRestocks =
      restocks?.reduce((acc, restock) => {
        return (acc += restock.quantity ?? 1)
      }, 0) ?? 0
    return totalReturnedItems === totalRestocks
  }

  const handleClickCancelRestock = () => {
    setRestockMode(false)
    setNewRestocks(newRestocksList)
  }

  const isPendingRefund =
    parseFloat(
      processDetails?.compensation?.statusSummary?.pendingRefundAmount
        ?.amount ?? '0'
    ) > 0

  return (
    <div className="relative flex h-full flex-col">
      {isFlagEnabled && <BlockedDashboardOverlay />}
      <div
        className={clsx('no-scrollbar overflow-y-scroll p-4 pt-0')}
        style={{
          height: restockMode ? 'calc(100% - 70px)' : undefined
        }}
      >
        <div className="flex w-full items-start justify-between py-6 pl-0">
          <ProcessInfo
            purchasedDate={processDetails?.purchasedDate}
            returnDate={processDetails?.returnDate ?? new Date()}
            customerPrintedOrderId={
              processDetails?.customerPrintedOrderId ?? ''
            }
            returnMethod={processLogistics?.return_method}
            amountUploadedFiles={processDetails?.returnDocuments?.length ?? 0}
            unauthorized={unauthorized}
            isRefunded={!isPendingRefund}
            isOnlyExchange={processDetails?.isOnlyExchange ?? false}
            commitedAmount={processDetails?.commitedAmount ?? 0}
            orderPlatformUrl={processDetails?.orderPlatformUrl ?? ''}
            platform={processDetails?.platform}
            isCanceled={processDetails?.isCanceled ?? false}
          />
          <ActionMenu
            isRestocked={isRestocked()}
            restockMode={restockMode}
            isClaim={processDetails?.isClaim ?? false}
            setRestockMode={setRestockMode}
            reviewMode={reviewMode}
            isKeepYourItem={processDetails?.isKeepYourItem ?? false}
            uploadDocsEnabled={processDetails?.uploadDocsEnabled ?? false}
            authPending={processDetails?.authorizationPending ?? false}
            unauthorized={unauthorized}
            canceled={processDetails?.isCanceled ?? false}
            isCancelable={isCancelable}
            isExchangeOnly={processDetails?.isOnlyExchange ?? false}
            hasExecutedStoreCreditRefund={
              processDetails?.hasExecutedStoreCreditRefund ?? false
            }
            hasReviews={returnedItems.some((item) => item.review)}
            customer={processDetails?.customer}
          />
        </div>

        {processDetails?.authorizationPending && (
          <AuthorizeBanner
            authorizeMode={authorizeMode}
            unauthorizeMode={unauthorizeMode}
            setAuthorizeMode={setAuthorizeMode}
            setUnauthorizeMode={setUnauthorizeMode}
            uploadDocsEnabled={processDetails?.uploadDocsEnabled ?? false}
            editAmountMode={editAmount}
            setEditAmountMode={setEditAmount}
          />
        )}
        {processDetails?.unauthorized && (
          <UnauthorizedStatusBanner processId={processDetails.processId} />
        )}

        {processDetails?.isClaim && <ClaimBanner />}
        {processDetails?.exchangeOrderShippedNotice && (
          <ExchangeShippedBanner
            notice={processDetails.exchangeOrderShippedNotice}
            orderNumber={processDetails.exchangeOrderNumber}
            orderUrl={processDetails.exchangeOrderUrl}
          />
        )}

        <IslandsContainer
          processDetails={processDetails}
          processLogistics={processLogistics}
          reviews={newReviewsList}
          restocks={newRestocks}
          setRestocks={setNewRestocks}
          returnedItems={returnedItems}
          notReceivedItems={notReceivedItems}
          returnDocuments={processDetails?.returnDocuments ?? []}
          reviewMode={reviewMode}
          setReviewMode={setReviewMode}
          restockMode={restockMode}
          unauthorizeMode={unauthorizeMode}
          setUnauthorizeMode={setUnauthorizeMode}
          authorizeMode={authorizeMode}
          setAuthorizeMode={setAuthorizeMode}
          unauthorized={unauthorized}
          editAmount={editAmount}
          setEditAmount={setEditAmount}
          isClaim={processDetails?.isClaim ?? false}
        />
      </div>
      {restockMode ? (
        <RestockActionBar
          processId={processDetails?.processId ?? ''}
          ecommerceId={processDetails?.ecommerceId ?? ''}
          restocks={newRestocks ?? []}
          handleClickCancel={handleClickCancelRestock}
        />
      ) : null}
    </div>
  )
}

function useCalculateProductsActions(
  processDetails: ReturnProcess | undefined,
  currentReviews: PerformedReview[],
  restocks: RestockItem[],
  restockLocations: Map<string, RestockLocation[]>,
  groupItems: boolean
) {
  const settings = useAppSelector((s) => s.settingsApi.settings.response)

  const defaultRestockStatus = settings.group
    ? defaultSelectedRestockBrands.includes(settings.group)
    : false

  const matchedItems = findMatchedItems(groupItems)

  const returnedItems = filterReturnedItems()
  const notReceivedItems = filterNotReceivedItems()

  const newReviewsList = calculateReviews()
  const newRestocksList = calculateRestocks()

  function filterReturnedItems() {
    return matchedItems.filter(
      (item) => item.returnReasonKey !== 'NOT_RECEIVED'
    )
  }

  function filterNotReceivedItems() {
    return matchedItems.filter(
      (item) => item.returnReasonKey === 'NOT_RECEIVED'
    )
  }

  function calculateReviews() {
    const returnedItemsReviews: NewReview[] =
      returnedItems.flatMap((item) => {
        const reviews: NewReview[] = []
        for (let i = 0; i < item.quantity; i++) {
          const rejectReason = currentReviews.find(
            (review) => review.reverLineItemId === item.reverLineItemId
          )?.rejectReason
          reviews.push({
            lineItemId: item.reverLineItemId,
            status: 'PENDING',
            shouldRestock: defaultRestockStatus,
            ...(defaultRestockStatus && {
              location: (
                restockLocations.get(item.product.variantId) ?? []
              ).find((loc) => loc.isDefault)
            }),
            rejectReason: rejectReason
          })
        }
        return reviews
      }) ?? []
    const notReceivedItemsReviews: NewReview[] =
      notReceivedItems.flatMap((item) => {
        const reviews: NewReview[] = []
        for (let i = 0; i < item.quantity; i++) {
          const rejectReason = currentReviews.find(
            (review) => review.reverLineItemId === item.reverLineItemId
          )?.rejectReason
          reviews.push({
            lineItemId: item.reverLineItemId,
            status: 'APPROVED',
            rejectReason: rejectReason,
            shouldRestock: false
          })
        }
        return reviews
      }) ?? []
    return returnedItemsReviews.concat(notReceivedItemsReviews)
  }

  function calculateRestocks() {
    const restockList: RestockItem[] =
      returnedItems.flatMap((item) => {
        const itemRestocks: RestockItem[] = []
        const oldRestocks = restocks.filter(
          (r) => r.lineItemId === item.reverLineItemId
        )
        restocks = restocks.filter((r) => r.lineItemId !== item.reverLineItemId)
        oldRestocks.forEach((r) => {
          for (let i = 0; i < (r.quantity ?? 1); i++) {
            itemRestocks.push({
              ...r,
              quantity: 1,
              status: 'RESTOCKED'
            })
          }
        })
        const totalAlreadyRestocked = itemRestocks.reduce((acc, r) => {
          return (acc += r.quantity ?? 1)
        }, 0)
        for (let i = 0; i < item.quantity - totalAlreadyRestocked; i++) {
          const defaultLocation = (
            restockLocations.get(item.product.variantId) ?? []
          ).find((loc) => loc.isDefault)
          itemRestocks.push({
            lineItemId: item.reverLineItemId,
            quantity: 1,
            status: defaultRestockStatus ? 'SHOULD_RESTOCK' : 'NOT_RESTOCKED',
            location: defaultLocation
          })
        }
        return itemRestocks
      }) ?? []
    return restockList
  }

  function findMatchedItems(groupItems: boolean) {
    if (groupItems) {
      if (currentReviews.length > 0) return unfoldItemsAndMatchAndGroupReviews()
      return getItems()
    }
    return unfoldItemsForReviewOrRestock()
  }

  function getItems() {
    return processDetails?.returnedItems ?? []
  }

  function unfoldItemsAndMatchAndGroupReviews() {
    return (
      processDetails?.returnedItems?.flatMap((item) => {
        const items: ReturnedItem[] = []
        const correspondingReviews = currentReviews.filter(
          (rev) => rev.reverLineItemId === item.reverLineItemId
        )
        const groupByStatus = (status: string) =>
          correspondingReviews.filter((rev) => rev.status === status)
        const approvedCorrespondingReviews = groupByStatus('APPROVED')
        const declinedCorrespondingReviews = groupByStatus('DECLINED')
        const missingCorrespondingReviews = groupByStatus('MISSING')
        const groupedReviews = [
          approvedCorrespondingReviews,
          declinedCorrespondingReviews,
          missingCorrespondingReviews
        ]
        groupedReviews.forEach((group) => {
          if (group.length > 0)
            items.push({
              ...item,
              quantity: group.length,
              review: group[0].status
            })
        })
        return items
      }) ?? []
    )
  }

  function unfoldItemsForReviewOrRestock() {
    return (
      processDetails?.returnedItems?.flatMap((item) => {
        const items: ReturnedItem[] = []
        if (item.quantity) {
          for (let i = 0; i < item.quantity; i++) {
            items.push({
              ...item,
              quantity: 1
            })
          }
        }
        return items
      }) ?? []
    )
  }

  return { returnedItems, notReceivedItems, newReviewsList, newRestocksList }
}
